import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FileText, Loader2, MessageCircle, ShieldX } from "lucide-react";
import {
  listAllPendingPurchases,
  approvePendingPurchase,
  rejectPendingPurchase,
  getPendingReceiptSignedUrl,
  resendActivationLink,
} from "@/lib/pending-purchase.functions";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const listQ = () =>
  queryOptions({
    queryKey: ["admin", "pending-purchases"],
    queryFn: () => listAllPendingPurchases(),
  });

export const Route = createFileRoute("/_authenticated/admin/pending-purchases")({
  loader: ({ context }) => context.queryClient.ensureQueryData(listQ()),
  head: () => ({
    meta: [
      { title: "الطلبات قيد المراجعة — إدارة" },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: Page,
});

function badge(status: string) {
  const map: Record<string, string> = {
    pending: "bg-amber-100 text-amber-900",
    approved: "bg-emerald-100 text-emerald-900",
    rejected: "bg-rose-100 text-rose-900",
  };
  const label: Record<string, string> = {
    pending: "قيد المراجعة",
    approved: "مقبول",
    rejected: "مرفوض",
  };
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${map[status] ?? "bg-slate-100 text-slate-900"}`}
    >
      {label[status] ?? status}
    </span>
  );
}

function Page() {
  const { data } = useSuspenseQuery(listQ());
  return (
    <div>
      <header className="mb-6">
        <p className="text-xs font-bold tracking-widest text-primary">إدارة</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold text-foreground">الطلبات قيد المراجعة</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          راجع إيصالات التحويل والدفع عبر جميع المنتجات. بعد الموافقة نُنشئ الحساب ونرسل رابط التفعيل عبر واتساب.
        </p>
      </header>

      {data.length === 0 ? (
        <div className="rounded-ds-lg bg-card p-10 text-center shadow-soft">
          <p className="text-sm text-muted-foreground">لا توجد طلبات بعد.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {data.map((p: any) => <Row key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}

function Row({ p }: { p: any }) {
  const qc = useQueryClient();
  const approve = useServerFn(approvePendingPurchase);
  const reject = useServerFn(rejectPendingPurchase);
  const getReceipt = useServerFn(getPendingReceiptSignedUrl);
  const resend = useServerFn(resendActivationLink);
  const [busy, setBusy] = useState<"approve" | "reject" | "receipt" | "resend" | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const isPending = p.status === "pending";

  async function openReceipt() {
    if (!p.receipt_file || busy) return;
    setBusy("receipt");
    try {
      const { url } = await getReceipt({ data: { path: p.receipt_file } });
      window.open(url, "_blank", "noopener");
    } catch (e: any) {
      toast.error(e?.message ?? "تعذّر فتح الإيصال.");
    } finally {
      setBusy(null);
    }
  }

  async function doResend() {
    if (busy) return;
    setBusy("resend");
    try {
      const res: any = await resend({ data: { pendingId: p.id } });
      const { waLink, magicLink } = res;
      if (res.existingAccount) {
        if (waLink) window.open(waLink, "_blank", "noopener");
        toast.warning(res.notice, { duration: 10000 });
      } else if (waLink) {
        window.open(waLink, "_blank", "noopener");
        toast.success("رابط جديد جاهز — أرسله عبر واتساب.");
      } else {
        try {
          await navigator.clipboard.writeText(magicLink);
          toast.success("نسخنا رابط الدخول الجديد.");
        } catch {
          toast.success("الرابط الجديد: " + magicLink);
        }
      }
      qc.invalidateQueries({ queryKey: ["admin", "pending-purchases"] });
    } catch (e: any) {
      toast.error(e?.message ?? "تعذّرت إعادة الإرسال.");
    } finally {
      setBusy(null);
    }
  }


  async function doApprove() {
    if (busy) return;
    setBusy("approve");
    try {
      const res: any = await approve({ data: { pendingId: p.id } });
      const { waLink, magicLink } = res;
      if (res.existingAccount) {
        if (waLink) window.open(waLink, "_blank", "noopener");
        toast.warning(`تم التفعيل. ${res.notice}`, { duration: 10000 });
      } else if (waLink) {
        window.open(waLink, "_blank", "noopener");
        toast.success("تم التفعيل — افتح محادثة واتساب لإرسال الرابط.");
      } else {
        try {
          await navigator.clipboard.writeText(magicLink);
          toast.success("تم التفعيل — نسخنا رابط الدخول.");
        } catch {
          toast.success("تم التفعيل. الرابط: " + magicLink);
        }
      }
      qc.invalidateQueries({ queryKey: ["admin", "pending-purchases"] });
    } catch (e: any) {
      toast.error(e?.message ?? "فشل التفعيل.");
    } finally {
      setBusy(null);
    }
  }

  async function doReject() {
    if (busy || rejectReason.trim().length < 2) return;
    setBusy("reject");
    try {
      const { waLink } = await reject({
        data: { pendingId: p.id, reason: rejectReason.trim() },
      });
      if (waLink) {
        window.open(waLink, "_blank", "noopener");
        toast.success("تم الرفض — افتح واتساب لإبلاغ العميلة.");
      } else {
        toast.success("تم رفض الطلب.");
      }
      setShowReject(false);
      setRejectReason("");
      qc.invalidateQueries({ queryKey: ["admin", "pending-purchases"] });
    } catch (e: any) {
      toast.error(e?.message ?? "فشل الرفض.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="rounded-ds-lg bg-card p-5 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-display text-base font-extrabold text-foreground">
              {p.full_name || "—"}
            </h3>
            {badge(p.status)}
            <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
              {p.product?.title_ar ?? p.product?.slug ?? "—"}
            </span>
            <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
              {p.payment_method === "paypal" ? "PayPal" : "تحويل داخلي (سوريا)"}
            </span>
            {p.country ? (
              <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
                {p.country}
              </span>
            ) : null}
            <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
              {p.environment === "live" ? "live" : "test"}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span>{p.email}</span>
            <span>{p.phone}</span>
            <span>
              {p.amount_cents != null
                ? `$${(p.amount_cents / 100).toFixed(2)} ${p.currency ?? "USD"}`
                : "—"}
            </span>
            <span>{new Date(p.created_at).toLocaleString("ar-u-nu-latn")}</span>
          </div>
          {p.reason && (
            <p className="mt-2 rounded-lg bg-rose-50 px-3 py-1.5 text-xs text-rose-900">
              سبب الرفض: {p.reason}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {p.receipt_file && (
            <button
              type="button"
              onClick={openReceipt}
              disabled={busy === "receipt"}
              className="inline-flex items-center gap-1.5 rounded-full bg-background px-4 py-2 text-xs font-bold text-foreground hover:bg-accent/30 disabled:opacity-50"
            >
              {busy === "receipt" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileText className="h-3.5 w-3.5" />
              )}
              الإيصال
            </button>
          )}
          {isPending && (
            <>
              <button
                type="button"
                onClick={doApprove}
                disabled={!!busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {busy === "approve" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <MessageCircle className="h-3.5 w-3.5" />
                )}
                تفعيل + واتساب
              </button>
              <button
                type="button"
                onClick={() => setShowReject((s) => !s)}
                disabled={!!busy}
                className="inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-transparent px-4 py-2 text-xs font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                <ShieldX className="h-3.5 w-3.5" /> رفض
              </button>
            </>
          )}
          {p.status === "approved" && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-4 py-2 text-xs font-bold text-emerald-900">
              <CheckCircle2 className="h-3.5 w-3.5" /> تم التفعيل
            </span>
          )}
          {p.status === "approved" && (
            <button
              type="button"
              onClick={doResend}
              disabled={!!busy}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {busy === "resend" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}
              إعادة إرسال رابط التفعيل
            </button>
          )}
          {p.status === "approved" && p.wa_notified_at && (
            <span className="text-xs text-muted-foreground">
              آخر إرسال: {new Date(p.wa_notified_at).toLocaleString("ar-u-nu-latn")}
            </span>
          )}
        </div>
      </div>
      {showReject && (
        <div className="mt-4 rounded-2xl bg-background/70 p-4">
          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-foreground">سبب الرفض (يُرسل عبر واتساب)</span>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowReject(false)}
              className="rounded-full bg-background px-4 py-2 text-xs font-bold text-muted-foreground"
            >
              إلغاء
            </button>
            <button
              type="button"
              onClick={doReject}
              disabled={busy === "reject" || rejectReason.trim().length < 2}
              className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-4 py-2 text-xs font-bold text-destructive-foreground shadow-soft hover:bg-destructive/90 disabled:opacity-50"
            >
              {busy === "reject" && <Loader2 className="h-3.5 w-3.5 animate-spin" />} تأكيد الرفض
            </button>
          </div>
        </div>
      )}
    </section>
  );
}